import {
  AUDIENCE_BLOCK_MESSAGE,
  AUDIENCE_BLOCK_REASON,
  AUDIENCE_MODE,
  CAMPAIGN_DESTINATIONS,
  CAMPAIGN_SECONDARY_DESTINATION,
  CHANNEL_SCOPE_OPTIONS,
  CONVERSION_ACTIONS,
  CONVERSION_ACTION_DESTINATIONS,
  DIRECT_RECIPIENT_BLOCK_MESSAGE,
  EMAIL_SEGMENT,
  isPhase1ConversionAction,
  isPhase1Destination,
  PHASE_1_CAMPAIGN_DESTINATIONS,
  PHASE_1_CONVERSION_ACTIONS,
  SUBSCRIPTION_FILTER_LABEL
} from 'lib';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  focusRequiredField,
  listFieldLabels,
  missingCampaignFields,
  missingToSaveDraft,
  REQUIRED_FIELD,
  requiredFieldId
} from './campaignRequirements';
import ChallengePicker from './ChallengePicker';
import ChannelPicker from './ChannelPicker';
import PreviewPane from './PreviewPane';
import RecipientPicker from './RecipientPicker';
import { templateName } from './TemplateList';
import { Btn, Field, GroupLabel, Note, Segmented, segmentChoice, Select, T, TextInput } from './ui';
import DetailRowsEditor from './DetailRowsEditor';

/**
 * Section 4 - the campaign builder.
 *
 * The live preview is `CampaignPreview` below, rendered by the page rather than by this component:
 * the merged setup page has a template editor above this card that wants the same pane, and one
 * page showing the same template twice was the thing worth avoiding.
 *
 * Only the campaign template can be picked, and now it is the only one *shown*. The other nine are
 * triggered by an account or billing event, and a control offering "Password Reset" to a filtered
 * audience of twelve thousand is one mis-click from an incident.
 *
 * They used to be listed here, disabled, on the reasoning that hiding them pretends they are not
 * there. That reasoning was about the catalog, where they genuinely are: the Templates page lists
 * all ten and every one of them opens for editing. This control is not the catalog — it is the
 * campaign's own template field, on a panel that only renders for a template that can be sent at
 * all. Nine permanently disabled options here answer a question nobody asked and make the one real
 * choice look like one of ten.
 *
 * The audience is not chosen here either. It is whatever Section 1's filters currently resolve to,
 * because those are the filters the endpoint stores on the campaign and re-resolves at send time.
 * Offering a second, independent audience picker would give an operator two sources of truth for
 * the same question.
 */

/** The four quick tags the design puts under the campaign name. */
const PreviewCard = styled.div`
  border: 1px solid ${T.border};
  border-radius: 12px;
  background: ${T.panel};
  overflow: hidden;
`;

const PreviewMeta = styled.div`
  padding: 13px 15px;
  border-bottom: 1px solid ${T.border};
  background: ${T.card};
  font-size: 12.5px;
  color: ${T.muted};
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    color: ${T.body};
    font-weight: 700;
  }
`;

/**
 * Describes the current filter set in one line, for the audience select.
 *
 * The design's option reads "Filtered Users (Active segment, Country: US, Team: Lakers)" - the
 * filters spelled out rather than a count. A count would be the more tempting label and the worse
 * one: it changes between reading it and sending, while the description is what actually gets
 * stored on the campaign.
 */
const describeFilters = (filters) => {
  const parts = [];
  if (filters.segments?.length) parts.push(`Segment: ${filters.segments.join('/')}`);
  if (filters.country?.length) parts.push(`Country: ${filters.country.join(', ')}`);
  if (filters.teams?.length) parts.push(`Team: ${filters.teams.join(', ')}`);
  if (filters.subscriptionTiers?.length) {
    parts.push(
      `Subscription: ${filters.subscriptionTiers
        .map((value) => SUBSCRIPTION_FILTER_LABEL[value] || value)
        .join(', ')}`
    );
  }
  if (filters.eventParticipation?.length) {
    parts.push(`${filters.eventParticipation.length} event(s)`);
  }
  if (filters.emailStatus) parts.push(`Email: ${filters.emailStatus}`);
  if (filters.lastActive?.from || filters.lastActive?.to) parts.push('Last active range');
  if (filters.signupDate?.from || filters.signupDate?.to) parts.push('Signup range');

  if (parts.length) return parts.join(', ');

  /*
    Never "every eligible fan" by default.

    That is what this said when the filters had been lost — which happened on every reload, because
    they lived only in memory. The composer then presented an unset audience as the whole audience,
    and Save Draft stored a campaign addressed to twelve thousand people. An unset audience and a
    deliberately unfiltered one are different states and must read differently.
  */
  return null;
};

/**
 * The three states a campaign can be archived from.
 *
 * Mirrors the backend's own list. Archiving is not a way to stop a send — the button stays disabled
 * while a campaign can still go out, and the tooltip points at cancel instead, because an operator
 * reaching for "Archive" to stop a campaign would otherwise watch it send anyway.
 */
/**
 * Paths for the resolved-address preview, keyed by destination.
 *
 * Read from the catalog rather than copied beside it. This was a second list, and it drifted: it
 * still said `/fancam` for the feed long after the backend had moved that entry to the app-install
 * page, so the composer showed an operator an address no campaign was sending. The catalog carries
 * `path` now, mirroring the backend's, and one list cannot disagree with itself.
 */
const DESTINATION_PATHS = Object.fromEntries(
  CAMPAIGN_DESTINATIONS.map((destination) => [destination.value, destination.path])
);

const destinationLabel = (value) =>
  CAMPAIGN_DESTINATIONS.find((option) => option.value === value)?.label || value;

/**
 * The words for a conversion action, including one no longer offered.
 *
 * Reads the whole vocabulary rather than the Phase 1 list, which is the point: a campaign saved
 * with `orderPlaced` must still read "Places an order" in the select, in the pairing warning and on
 * the report card. Falling back to the raw value is the last resort, not the ordinary path — a
 * screen showing `challengeEntered` at an operator is a defect a previous feature already fixed
 * once.
 */
const conversionActionLabel = (value) =>
  CONVERSION_ACTIONS.find((option) => option.value === (value || ''))?.label || value;

/**
 * Who can open a challenge, which is the only state a challenge actually has.
 *
 * There is no open/closed lifecycle on a FanCam — no end date, no deadline, no status. It exists
 * until it is deleted. What varies is visibility, and that is what decides whether the fan who
 * opened the email can see the thing it sent them to. Returning `null` for the ordinary case keeps
 * the picker quiet about the challenges that need nothing said.
 */
const reachability = (challenge) => {
  if (!challenge) return null;
  if (challenge.shareWith === 'noOne') return 'visible to nobody';
  if (challenge.shareWith === 'friends') return "visible to the owner's friends only";
  if (challenge.subscriptionOnly) return 'subscribers only';
  return null;
};

/**
 * Whether the rendered email opens with the photographic banner.
 *
 * Only welcome and campaign do, which is what the Figma boards draw. The other seven open on the
 * logo header, and they were still offered the banner's language switch and its "Edit banner
 * wording" link. Neither control can change those emails. The answer is read off the render
 * rather than listed here, the same way the server decides whether to include the banner's
 * stylesheet: its layout looks for the `p4p-hero` section. A class *list* that merely starts with
 * `p4p-hero-` (the bar, the text column) is not the banner section.
 */
const HERO_SECTION = /class="p4p-hero[" ]/;

export const drawsBanner = (preview) =>
  HERO_SECTION.test(preview?.desktop || preview?.mobile || '');

/**
 * The live preview, as its own thing — because one page now has three cards that all want it.
 *
 * It renders beside the builder in the design, and it did live inside it. On the merged page the
 * template editor sits directly above, and a second pane for the same template a card apart is
 * exactly the inconsistency the merge removes. So the page owns one pane and decides where it
 * goes: beside the cards where there is room for a 584px email at its real width, below the
 * builder where there is not.
 *
 * `subject` rather than reading `blocks` here: what the inbox shows is the title being edited
 * upstairs, and the editor is the thing that knows it.
 */
export const CampaignPreview = ({
  preview,
  previewLoading,
  previewVariant,
  previewVariantRequest,
  /*
    The banner language, passed straight through to the pane.

    Held nowhere on this component and written nowhere on the campaign: it arrives from the shell's
    view-state and goes to the preview, which is the whole of its life. Nothing between here and
    `saveCampaign` can see it, which is how it stays a viewing control.
  */
  previewLocale,
  previewLocales,
  onPreviewLocaleChange,
  onRefreshPreview,
  subject
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap'
      }}
    >
      <GroupLabel>Live template preview</GroupLabel>
      <Btn variant="outline" icon="Refresh" onClick={onRefreshPreview} disabled={previewLoading}>
        {previewLoading ? 'Rendering...' : 'Refresh preview'}
      </Btn>
    </div>

    <PreviewCard>
      <PreviewMeta>
        <span>
          {/*
                The configured sender, from the preview response.

                This used to fall back to a hardcoded address, and `previewTemplate` did not return
                `from` at all — so the fallback was *always* what showed, presenting a constant in
                this file as though it were configuration. An operator checking the sender before a
                send to twelve thousand people was reading a lie. No fallback now: if the field is
                missing the label says so rather than inventing an address.
              */}
          From: <strong>{preview?.from || <span style={{ color: T.faint }}>—</span>}</strong>
        </span>
        <span>
          Subject: <strong>{subject || preview?.subject || '-'}</strong>
        </span>
      </PreviewMeta>
      <PreviewPane
        unrecognisedTags={preview?.unrecognisedTags}
        mobile={preview?.mobile}
        desktop={preview?.desktop}
        loading={previewLoading}
        variant={previewVariant}
        variantRequest={previewVariantRequest}
        locale={previewLocale}
        locales={drawsBanner(preview) ? previewLocales : undefined}
        onLocaleChange={onPreviewLocaleChange}
        bannerHref={drawsBanner(preview) ? '/admin/email/banner' : undefined}
      />
    </PreviewCard>
  </div>
);

const CampaignBuilder = ({
  templates = [],
  selectedKey,
  onSelectTemplate,
  campaignName,
  onCampaignName,
  filtersWereSet,
  detailRows,
  conversionAction,
  onConversionAction,
  destination,
  onDestination,
  destinationTarget,
  onDestinationTarget,
  challenges = [],
  challengesLoading,
  challengesHasMore,
  challengeResolved,
  onDetailRows,
  rowsReadOnlyReason,
  filters,
  onSegmentShortcut,
  audienceMode,
  onAudienceMode,
  channels,
  channelId,
  channelLabel,
  channelsLoading,
  relationshipScope,
  onChannelAudience,
  channelAudience,
  recipients,
  recipientSearch,
  onRecipientSearch,
  recipientsLoading,
  recipientId,
  recipientLabel,
  onDirectRecipient,
  directRecipient,
  audienceTotal,
  matching,
  onInsertTag,
  onSaveDraft,
  campaignStatus,
  campaignId,
  busy,
  isRunning,
  /*
    Shows every required field's message, not only the name's. Set by the scheduler's list of what
    is missing, so the fields it names are marked in red when the operator comes back up to them.
  */
  showRequired = false
}) => {
  const channelMode = audienceMode === AUDIENCE_MODE.CHANNEL_SPECIFIC;

  const recipientMode = audienceMode === AUDIENCE_MODE.DIRECT_RECIPIENT;
  /** True for either criteria-based mode: the modes that supply their own final count. */
  const criteriaMode = channelMode || recipientMode;

  /*
    What is still empty, from the same list the scheduler reads.

    A draft needs only its name, so pressing Save Draft reveals only the name's message; the rest are
    revealed by the scheduler, which is where they stop the campaign.
  */
  const [saveAttempted, setSaveAttempted] = useState(false);
  const missing = missingCampaignFields({
    campaignName,
    destination,
    destinationTarget,
    challengeResolved,
    audienceMode: criteriaMode ? audienceMode : AUDIENCE_MODE.SAVED_SEGMENT,
    filtersWereSet,
    channelId,
    recipientId
  });
  const missingForDraft = missingToSaveDraft(missing);
  const errorFor = (key) => {
    const field = missing.find((entry) => entry.key === key);
    if (!field) return undefined;
    return showRequired || (saveAttempted && key === REQUIRED_FIELD.NAME)
      ? field.message
      : undefined;
  };

  /*
    Save Draft is pressable with no name, and says why it did not save.

    It used to be disabled, which is a button that does nothing and says nothing — the report was
    that a campaign could not be saved and nobody could tell why.
  */
  const saveDraft = () => {
    if (missingForDraft.length) {
      setSaveAttempted(true);
      focusRequiredField(missingForDraft[0].key);
      return;
    }

    setSaveAttempted(false);
    onSaveDraft();
  };

  /**
   * Whether the channel this campaign names can still be used.
   *
   * Read from the server's block reason rather than inferred from an empty picker list. A channel
   * missing from the search results has merely not been searched for; a channel the server calls
   * `channelUnavailable` is archived, suspended, or no longer a channel — and only one of those
   * two states should mark an option as gone.
   */
  const channelGone = channelAudience?.blockReason === AUDIENCE_BLOCK_REASON.CHANNEL_UNAVAILABLE;

  /*
    Only once a count has actually been attempted.

    `status` starts as `idle` with `canSend: false`, which is not a refusal — it is nothing having
    been asked yet. Rendering a block message from it would tell an operator their audience was
    empty before they had chosen a channel.
  */
  const blockMessage =
    channelMode && channelId && channelAudience?.status === 'ready' && channelAudience.blockReason
      ? AUDIENCE_BLOCK_MESSAGE[channelAudience.blockReason]
      : null;

  /** The account this campaign names, gone or suspended. The recipient mode's `channelGone`. */
  const recipientGone =
    directRecipient?.blockReason === AUDIENCE_BLOCK_REASON.RECIPIENT_UNAVAILABLE;

  /*
    The same `idle`-is-not-a-refusal rule as the channel path above.

    Worded from `DIRECT_RECIPIENT_BLOCK_MESSAGE`, which differs from the channel table in exactly
    one entry — the one that would otherwise tell an operator that "nobody in this channel
    audience" can be emailed about a single named person they are looking straight at.
  */
  const recipientBlockMessage =
    recipientMode &&
    recipientId &&
    directRecipient?.status === 'ready' &&
    directRecipient.blockReason
      ? DIRECT_RECIPIENT_BLOCK_MESSAGE[directRecipient.blockReason]
      : null;

  /*
    The destination's own catalog entry, and the two things derived from it.

    `destinationLabel` is a lookup rather than a second copy of the names: the catalog is mirrored
    from the backend enum, and a label typed here as well would be the thing that drifts when a
    destination is added.
  */
  /*
    No default applied here any more.

    This read `destination || DEFAULT_CAMPAIGN_DESTINATION`, so an untouched campaign looked as
    though it had chosen the FanCam feed. That was true while an unset destination really did send
    to the feed. Phase 1 requires a named challenge, and there is no sensible default challenge — so
    an unset destination is now an unfinished campaign, and showing it as a resolved address would
    be the screen answering a question the operator has not answered.
  */
  const chosenDestination = CAMPAIGN_DESTINATIONS.find((option) => option.value === destination);

  /*
    Built from the browser's own origin, because the CMS is served from the site root the emails
    link to. An operator seeing the address they are about to send is the point of the control.
  */
  /*
    Where the secondary link goes.

    The team is resolved at the send — the campaign's own channel, or the default team when the
    audience names no channel — so the composer shows the shape rather than inventing a username it
    would have to guess. Showing `<team>` is the same honesty the primary preview uses for a target
    the operator has not typed yet.
  */
  const secondaryPreview = `${window.location.origin}${(
    DESTINATION_PATHS[CAMPAIGN_SECONDARY_DESTINATION] || ''
  ).replace(':target', '<team>')}`;

  const destinationPreview = (() => {
    if (!chosenDestination) return '';

    // `:team` is resolved at the send from the picked challenge, so it previews as its shape.
    const path = (DESTINATION_PATHS[chosenDestination.value] || '').replace(':team', '<team>');
    const target = (destinationTarget || '').trim();
    const resolved = chosenDestination.target
      ? path.replace(
          ':target',
          target ? encodeURIComponent(target) : `<${chosenDestination.target}>`
        )
      : path;

    return `${window.location.origin}${resolved}`;
  })();

  /*
    A challenge most of the audience cannot open.

    Warned, never refused. A subscriber-only challenge sent to a subscriber segment is exactly
    right, and refusing it would make a correct campaign impossible to send. What is not acceptable
    is finding out from a fan.
  */
  const reachabilityWarning = (() => {
    const limited = reachability(challengeResolved);
    if (!limited) return null;

    return challengeResolved.shareWith === 'noOne'
      ? 'This challenge is visible to nobody, so every recipient will follow the button to a page they cannot see.'
      : `This challenge is ${limited}. Recipients outside that group will follow the button to a page they cannot see — which is right if that is who this campaign is for, and worth checking if it is not.`;
  })();

  const pairingWarning = (() => {
    if (!conversionAction || !chosenDestination) return null;

    const reachable = CONVERSION_ACTION_DESTINATIONS[conversionAction] || [];
    if (reachable.includes(chosenDestination.value)) return null;

    const actionLabel = conversionActionLabel(conversionAction);

    return reachable.length
      ? `A fan sent to "${chosenDestination.label}" cannot ${actionLabel.toLowerCase()} there, so CTA Conversion will read 0%. Point the button at ${reachable
          .map((value) => `"${destinationLabel(value)}"`)
          .join(' or ')}, or change the conversion action.`
      : `Nothing in the product lets a fan ${actionLabel.toLowerCase()} from an email, so CTA Conversion will read 0% whatever destination you choose.`;
  })();

  /*
    The display name, not the raw key — the same correction the detail band needed.

    This select listed `campaign`, `passwordReset`, `subscriptionConfirmation`. The catalog cards
    two clicks earlier call them "Campaign Email", "Password Reset" and "Subscription
    Confirmation", so the one place an operator picks a template was the one place it was named
    differently.
  */
  const templateOptions = templates
    .filter((template) => template.campaignEligible)
    .map((template) => ({ value: template.key, label: templateName(template.key) }));

  /*
    One eligible template is the shape of the product today, so it is stated rather than chosen.

    A `<select>` holding a single option is a control that cannot do anything: it invites a click,
    opens, and offers the operator what they already have. When a second campaign-eligible template
    exists this becomes a real choice again without anyone editing this file.
  */
  const templateIsFixed = templateOptions.length <= 1;
  const fixedTemplate = templateOptions[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Note>
        Fields marked <span style={{ color: T.red, fontWeight: 700 }}>*</span> are required to
        schedule or send. A draft can be saved with just its name.
      </Note>

      <Field label="Campaign Name" required error={errorFor(REQUIRED_FIELD.NAME)}>
        <TextInput
          id={requiredFieldId(REQUIRED_FIELD.NAME)}
          value={campaignName}
          maxLength={120}
          placeholder="Lakers Season Ticket Holders Special Drop"
          onChange={(event) => onCampaignName(event.target.value)}
        />
      </Field>

      {/*
          The detail card's rows, and now the only place this card states a fact about the event.

          Here rather than in the Template Editor above, because they are per campaign: the template
          decides what a campaign *starts* with, and two campaigns on the same template state
          different facts.

          Two fixed inputs — Event Date and Next Action — used to sit above this editor feeding two
          fixed rows. A row carries its own label and its own value, so those inputs said the same
          things a second time in words the operator had not chosen. An operator writes "Tip-off" or
          "Venue" as a row instead.
        */}
      {/*
          The card's facts are rows, and only rows.

          Event Date and Next Action fields stood here twice: removed on 22 Sept, brought back on 30
          Sept because the rows editor still offered + Event Date and + Next Action chips with no
          value behind them, and removed again at the operator's request on 1 Oct. This time the
          chips and the default rows that used those tags went with them, so nothing on this page
          points at a value the page cannot set. An operator writes "Tip-off" | "Sat 12 Oct" as a
          row. Campaigns saved earlier keep their stored values, which still resolve.
        */}
      <DetailRowsEditor
        rows={detailRows}
        onChange={onDetailRows}
        busy={busy}
        readOnlyReason={rowsReadOnlyReason}
        revealEmpty={saveAttempted || showRequired}
      />

      {/*
          Where the button goes.

          Above Conversion Action deliberately: an operator chooses where a fan lands before
          choosing what counts as them having arrived. Before this control the dispatcher sent every
          campaign to the site root, so the one thing meant to carry a fan into the product carried
          them to a front door — and the composer let the button's words be edited but not its
          destination.
        */}
      <Field label="Button Destination" required error={errorFor(REQUIRED_FIELD.DESTINATION)}>
        <Select
          id={requiredFieldId(REQUIRED_FIELD.DESTINATION)}
          value={destination || ''}
          onChange={(event) => onDestination(event.target.value)}
        >
          <option value="">Choose where the button goes</option>
          {PHASE_1_CAMPAIGN_DESTINATIONS.map((value) => (
            <option key={value} value={value}>
              {destinationLabel(value)}
            </option>
          ))}

          {/*
            A stored destination that is no longer offered stays selectable, and that is the whole
            point of keeping it.

            Nine destinations are deferred for Phase 1, and campaigns saved before the narrowing
            still carry them. Dropping the option would leave the select showing an unrelated value
            for a campaign that was never edited — so the operator would be told their campaign
            pointed somewhere it did not. It is listed, labelled as deferred, and left alone unless
            they change it themselves.
          */}
          {destination && !isPhase1Destination(destination) ? (
            <option
              value={destination}
            >{`${destinationLabel(destination)} — deferred, not part of Phase 1`}</option>
          ) : null}
        </Select>
      </Field>

      {/*
        Deferred rather than missing.

        Without this the narrowing reads as a capability that was taken away. It was not: the nine
        other destinations are built and the backend still accepts them; only one has been proven end
        to end, and a campaign is a send to twelve thousand people.
      */}
      <Note>
        Phase 1 sends every campaign button to a FanCam challenge. The other destinations are built
        and deferred, not removed.
      </Note>

      {/*
        The challenge, chosen by name.

        This was a text box labelled "FanCam ID" holding a Mongo id — a field that could only be
        filled in correctly by someone with a database client, and silently wrong for everyone else.
        A wrong id is not a validation error; it is a live campaign whose button opens nothing.

        Every challenge is loaded (QA: older ones could not be chosen at all), and the picker shows
        each one's thumbnail with a search box at the top, because many challenges have no caption
        and a list of "Untitled challenge" could not be told apart.
      */}
      {destination === 'fancam' ? (
        <>
          <Field label="FanCam Challenge" required error={errorFor(REQUIRED_FIELD.CHALLENGE)}>
            {challengesLoading && !challenges.length ? (
              <Note>Loading challenges…</Note>
            ) : !challenges.length ? (
              /*
                An empty list is not an empty control.

                A bare `<select>` with nothing in it looks broken and tells an operator nothing about
                why. With the search box gone there is only one thing an empty list can mean, so it
                is said plainly rather than guessed at.
              */
              <Note style={{ color: T.amber }}>
                There are no FanCam challenges yet. A campaign cannot be sent until one exists.
              </Note>
            ) : (
              <ChallengePicker
                id={requiredFieldId(REQUIRED_FIELD.CHALLENGE)}
                challenges={challenges}
                value={destinationTarget || ''}
                onChange={onDestinationTarget}
                noteFor={reachability}
              />
            )}
          </Field>

          {challengesHasMore ? (
            <Note>Older challenges could not all be loaded. Search finds only those listed.</Note>
          ) : null}

          {/*
            The stored challenge, checked rather than assumed.

            `undefined` is "not asked yet" and says nothing. `null` is "asked, and it is gone" — a
            campaign whose button leads nowhere, which must be caught here rather than by a fan.
          */}
          {destinationTarget &&
          challengeResolved === null &&
          !errorFor(REQUIRED_FIELD.CHALLENGE) ? (
            <Note style={{ color: T.red }}>
              The challenge this campaign points at no longer exists. Choose another before sending.
            </Note>
          ) : null}

          {reachabilityWarning ? (
            <Note style={{ color: T.amber }}>{reachabilityWarning}</Note>
          ) : null}
        </>
      ) : null}

      {/*
          The resolved address and which app the tap opens.

          Both stated before sending rather than discovered from a fan's complaint: an operator
          picking "The store" should know a fan with the app installed still gets a browser, because
          no app screen exists for it.
        */}
      {/*
        Both addresses, before the send rather than after a fan reports one.

        The secondary is stated even though the operator cannot change it. They can change its
        *wording* on the Templates page, and a link whose words they wrote and whose destination they
        have never been shown is exactly the pairing that gets sent by mistake.
      */}
      {chosenDestination ? (
        <Note>
          {destinationPreview}
          {' - '}
          {chosenDestination.appBacked
            ? 'opens the app for fans who have it, the web page for everyone else.'
            : 'opens a browser for everyone, including fans with the app.'}
          <br />
          {secondaryPreview}
          {' - '}
          the secondary link: the rewards of this campaign's channel, or of the default team when
          the audience names no channel. Its wording is edited on the Templates page; where it goes
          is not a per-campaign choice.
        </Note>
      ) : null}

      {/*
          The pairing an operator cannot see is wrong.

          A campaign sending fans to the FanCam feed while counting subscriptions can only ever
          report zero conversions, which reads as fans declining rather than as a journey that was
          never possible. Warned rather than refused: `rewardClaimed` has no valid destination at
          all, so refusing would make a selectable option unusable without removing it.
        */}
      {pairingWarning ? <Note style={{ color: T.amber }}>{pairingWarning}</Note> : null}

      {/*
          What this campaign counts as success.

          Optional, and the empty option is a real answer: a campaign with none set shows CTA
          Conversion as not measured rather than as 0%, so an operator is never told nobody
          converted when nobody ever said what converting meant.
        */}
      <Field label="Conversion Action">
        <Select
          value={conversionAction || ''}
          onChange={(event) => onConversionAction(event.target.value)}
        >
          <option value="">{conversionActionLabel('')}</option>
          {PHASE_1_CONVERSION_ACTIONS.map((value) => (
            <option key={value} value={value}>
              {conversionActionLabel(value)}
            </option>
          ))}

          {/*
            A stored action that is no longer offered stays selectable, for the same reason a stored
            destination does — and this select needs it more.

            Four of the five actions are unreachable from the one destination Phase 1 offers, so
            they are no longer offered either. But campaigns saved before that still carry them, and
            this is a *controlled* select: with no matching option, React renders it blank. The
            operator would be shown a campaign with no conversion action beside a report card
            counting one.
          */}
          {conversionAction && !isPhase1ConversionAction(conversionAction) ? (
            <option
              value={conversionAction}
            >{`${conversionActionLabel(conversionAction)} — deferred, not part of Phase 1`}</option>
          ) : null}
        </Select>
      </Field>

      <Field label="Template">
        {templateIsFixed ? (
          <Note>
            {fixedTemplate
              ? `${fixedTemplate.label} — the template a campaign is sent from.`
              : 'No template can be sent to an audience.'}
          </Note>
        ) : (
          <Select value={selectedKey} onChange={(event) => onSelectTemplate(event.target.value)}>
            {templateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Select Audience Segment">
        {/*
          Which of the two audiences this campaign uses, above the controls for either of them.

          A pair of radio-style buttons rather than a third entry in the segment dropdown. The two
          modes are not two segments — they are two different questions about who receives the
          campaign, and folding one into the other's control is how an operator ends up sending to
          an audience they did not choose (FR-013).
        */}
        <Segmented
          label="Audience mode"
          value={criteriaMode ? audienceMode : AUDIENCE_MODE.SAVED_SEGMENT}
          options={[
            /*
              The third mode, named for what it does rather than for what it is for.

              Not "Test send" and not "Developer mode". It is an ordinary campaign with an audience
              of one, and labelling it as a test would invite exactly the misreading that makes it
              dangerous — that the email is somehow not real, or is not counted, or cannot reach
              somebody who did not expect it. It is real, it is counted, and it reaches whoever is
              chosen. The proof send on the scheduler below is the one that is not.
            */
            { value: AUDIENCE_MODE.DIRECT_RECIPIENT, label: 'One person' },
            // In the order QA asked for: one person, one channel, then a saved audience.
            { value: AUDIENCE_MODE.CHANNEL_SPECIFIC, label: 'A channel’s fans' },
            { value: AUDIENCE_MODE.SAVED_SEGMENT, label: 'Saved audience' }
          ]}
          onChange={onAudienceMode}
        />
      </Field>

      {channelMode ? (
        <>
          <ChannelPicker
            selectId={requiredFieldId(REQUIRED_FIELD.AUDIENCE)}
            required
            error={errorFor(REQUIRED_FIELD.AUDIENCE)}
            channels={channels}
            channelId={channelId}
            channelLabel={channelLabel}
            loading={channelsLoading}
            unavailable={channelGone}
            onSelect={(channel) => onChannelAudience(channel, relationshipScope)}
          />

          <Field
            label="Who at that channel"
            hint={CHANNEL_SCOPE_OPTIONS.find((option) => option.value === relationshipScope)?.hint}
          >
            <Select
              value={relationshipScope}
              disabled={!channelId}
              onChange={(event) =>
                onChannelAudience({ id: channelId, label: channelLabel }, event.target.value)
              }
            >
              {CHANNEL_SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          {/*
            The count, and the reason there is not one.

            Four states, because they mean four different things to an operator about to send:
            nothing chosen yet, still counting, counted, and counted-and-blocked. A single line
            showing "0 fans" for all of them would say something false in three of the four —
            most dangerously in the loading case, where the real answer might be twelve thousand.

            Announced as well as shown, like the dashboard's own matching count: this number is
            the answer to "who will get this", and a screen-reader user changing the scope got no
            feedback at all from the version of this control that did not say so.
          */}
          <Note role="status" aria-live="polite">
            {!channelId
              ? 'Choose a channel to see how many fans this campaign would reach.'
              : channelAudience.status === 'loading'
                ? 'Counting eligible fans…'
                : channelAudience.eligibleRecipients === null
                  ? 'Eligible fans: not available'
                  : `Audience: ${channelAudience.eligibleRecipients.toLocaleString()} eligible ${
                      channelAudience.eligibleRecipients === 1 ? 'fan' : 'fans'
                    }${channelAudience.label ? ` — ${channelAudience.label}` : ''}`}
          </Note>

          {/*
            Why it cannot send, in the operator's words rather than the server's enum.

            Amber for a channel that has gone and for a count that failed, red for an audience of
            nobody: the first two are conditions to resolve, the third is the campaign as composed
            being undeliverable.
          */}
          {blockMessage ? (
            <Note
              style={{
                color:
                  channelAudience.blockReason === AUDIENCE_BLOCK_REASON.ZERO_ELIGIBLE_RECIPIENTS
                    ? T.red
                    : T.amber
              }}
            >
              {blockMessage}
            </Note>
          ) : null}

          {/*
            The count is final, and saying so matters here.

            Everywhere else on this screen the audience figure is labelled an estimate, because the
            filters are re-run at send time and the number moves. This one has already had the
            unsubscribed, unverified and suppressed taken out — but the criteria are still resolved
            at send time, so a fan who unfollows tomorrow is still dropped tomorrow. Both halves are
            true and an operator needs both.
          */}
          <Note>
            Unsubscribed, unverified and suppressed fans are already excluded from this figure. The
            fans themselves are worked out again when the campaign actually sends, so a fan who
            leaves the channel before then will not receive it.
          </Note>
        </>
      ) : recipientMode ? (
        <>
          <RecipientPicker
            selectId={requiredFieldId(REQUIRED_FIELD.AUDIENCE)}
            required
            error={errorFor(REQUIRED_FIELD.AUDIENCE)}
            recipients={recipients}
            userId={recipientId}
            recipientLabel={recipientLabel}
            /*
              The address from the count response, not from the picker's option list.

              These are the same value almost always, and the exception is the case that matters:
              an operator reopens a campaign whose recipient is not in the current twenty, so the
              list has no entry to read an address from while the server does. Taking it from the
              server means the line under the control is answering about the account the send will
              actually resolve.
            */
            recipientEmail={directRecipient?.recipientEmail || ''}
            search={recipientSearch}
            onSearch={onRecipientSearch}
            loading={recipientsLoading}
            unavailable={recipientGone}
            onSelect={onDirectRecipient}
          />

          {/*
            The count, in the four states the channel path has, saying different things in three of
            them — because for an audience of one the interesting number is never the number.
          */}
          <Note role="status" aria-live="polite">
            {!recipientId
              ? 'Choose an account to see whether it can receive this campaign.'
              : directRecipient.status === 'loading'
                ? 'Checking whether this account can be emailed…'
                : directRecipient.eligibleRecipients === null
                  ? 'This account could not be checked.'
                  : directRecipient.eligibleRecipients > 0
                    ? `Ready to send to 1 recipient${
                        directRecipient.label ? ` — ${directRecipient.label}` : ''
                      }`
                    : 'This account cannot be emailed right now, so this campaign would reach nobody.'}
          </Note>

          {recipientBlockMessage ? (
            <Note
              style={{
                color:
                  directRecipient.blockReason === AUDIENCE_BLOCK_REASON.ZERO_ELIGIBLE_RECIPIENTS
                    ? T.red
                    : T.amber
              }}
            >
              {recipientBlockMessage}
            </Note>
          ) : null}

          {/*
            What this mode is and, just as importantly, what it is not.

            Two sentences an operator needs before they use it for the first time: this is a real
            campaign — counted, fatigue-limited, recorded — which is precisely what makes it worth
            sending, and it is not the proof copy on the scheduler below, which goes to the signed-in
            administrator and is excluded from all of that. Someone who confuses the two either
            mails a stranger thinking they are testing, or reads a proof and believes they have
            tested the send path. Both are worth two sentences.
          */}
          <Note>
            This is an ordinary campaign with an audience of one. It is recorded in the campaign’s
            statistics, counts against the recipient’s fatigue limit, and is sent from the pinned
            template version — unlike Send Test below, which mails a marked proof copy to your own
            address and is excluded from all three.
          </Note>
        </>
      ) : (
        <Field label="Saved audience segment" required error={errorFor(REQUIRED_FIELD.AUDIENCE)}>
          <Select
            id={requiredFieldId(REQUIRED_FIELD.AUDIENCE)}
            value={filters.segments?.length === 1 ? filters.segments[0] : ''}
            onChange={(event) => onSegmentShortcut(event.target.value || null)}
          >
            <option value="">
              {filtersWereSet
                ? `Filtered Users (${describeFilters(filters) || 'every eligible fan'})`
                : 'No audience set — choose one on the Audience page'}
            </option>
            {Object.values(EMAIL_SEGMENT).map((segment) => (
              <option key={segment} value={segment}>
                {segmentChoice(segment)}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {/*
          No chip row on this card, twice over.

          The first one appended the chosen variable to `blocks.body` — a field this page has no
          editor for, which nothing persisted and nothing sent, so pressing a chip changed nothing
          an operator could see. The second wrote into Event Date and Next Action, and went when
          those fields did: a chip needs a text field to land in, and this card no longer has one.
          Insertion lives on the Templates editor and in the detail-rows editor above, both of which
          work at the caret of a field that is actually on screen.
        */}

      {/*
        The number, without the paragraph that used to follow it.

        That paragraph explained that a draft stores the filters rather than the list, and that they
        are resolved again at send time. True, and said in three other places — on the dashboard's
        audience card, on the campaign list, and in the send confirmation, which is the one an
        operator is actually reading when it matters. What is left is the count, which is data.
      */}
      {/* The filter path's own count. The criteria-based modes each have their own, stated above. */}
      {criteriaMode ? null : (
        <Note>
          {`Audience: ${(matching ?? 0).toLocaleString()} of ${(
            audienceTotal ?? 0
          ).toLocaleString()} fans`}
        </Note>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {/*
          Each button reports its own action; they used to relabel each other.

          "Save Changes" rather than "Update Draft" for a campaign that already exists: the four
          save buttons across this screen were "Save Template", "Save policy", "Save Compliance
          Settings" and "Update Draft" — three capitalisation styles and two verbs for the same
          act. They are all "Save X" in title case now, and this one still says which of the two
          things it is about to do.
        */}
        <Btn onClick={saveDraft} disabled={busy}>
          {isRunning('save-draft') ? 'Saving…' : campaignId ? 'Save Changes' : 'Save Draft'}
        </Btn>
        {/*
          Duplicate and Archive are held back, at QA's request, the same way the campaign list holds
          back its Open and Archive. The handlers (`duplicate`, `setArchived`) and their endpoints
          are untouched, so restoring either is a matter of rendering its button here again.
        */}
      </div>

      {saveAttempted && missingForDraft.length ? (
        <Note role="alert" style={{ color: T.red }}>
          {`Not saved. Complete the required ${
            missingForDraft.length === 1 ? 'field' : 'fields'
          } first: ${listFieldLabels(missingForDraft)}.`}
        </Note>
      ) : null}

      {/*
        Two notes stood here — where a saved draft turns up, and that scheduling needs one first —
        and both are said where they are acted on. The scheduler's own Schedule button carries
        "Save a campaign draft in the builder above first" as its reason, and a saved draft appears
        in the campaign list beside it a moment later. Repeating them under the buttons was a third
        telling, in the card an operator has already finished with.
      */}
    </div>
  );
};

export default CampaignBuilder;
